import type { TargetMemoryInternal } from './AssetMemoryReportBuilder';
import type { AssetMemoryAssetReference, AssetMemoryBlockName, AssetMemoryDiagnostic } from './AssetMemoryTypes';

/**
 * Resolves the target list for an <images> or <fonts> block.
 * A memory assignment needs a concrete target in multi-target projects; unassigned assets are shown for all targets.
 */
export const resolveBlockTargetNames = (
    block: Element,
    memoryName: string | null,
    targetInternalsByName: Map<string, TargetMemoryInternal>,
    diagnostics: AssetMemoryDiagnostic[]
): string[] | null => {
    const blockName: AssetMemoryBlockName = block.tagName.toLowerCase() as AssetMemoryBlockName;
    const targetAttr: string | null = block.getAttribute('if_target')?.trim() || null;

    if (!targetAttr) {
        if (targetInternalsByName.size === 1) return [Array.from(targetInternalsByName.keys())[0]];
        if (!memoryName) return Array.from(targetInternalsByName.keys());

        diagnostics.push({
            severity: 'error',
            code: 'missing-asset-target',
            blockName,
            message: `<${blockName}> must specify target when project.xml defines multiple targets.`
        });
        return null;
    }

    const targetNames: string[] = Array.from(
        new Set(
            targetAttr
                .split('|')
                .map((targetName: string) => targetName.trim())
                .filter(Boolean)
        )
    );

    if (targetNames.length === 0) {
        diagnostics.push({
            severity: 'error',
            code: 'empty-asset-target',
            blockName,
            message: `<${blockName}> has an empty target attribute.`
        });
        return null;
    }

    const missingTargetNames: string[] = targetNames.filter(
        (targetName: string) => !targetInternalsByName.has(targetName)
    );
    if (missingTargetNames.length > 0) {
        diagnostics.push({
            severity: 'error',
            code: 'unknown-asset-target',
            blockName,
            message: `<${blockName}> references unknown target(s): ${missingTargetNames.join(', ')}.`
        });
        return null;
    }

    return targetNames;
};

/**
 * Validates that a block-level memory name exists in every selected target.
 * This is what makes compound targets safe: the same memory name must be available everywhere.
 */
export const validateMemoryForTargets = (
    blockName: AssetMemoryBlockName,
    memoryName: string | null,
    targetNames: string[],
    targetInternalsByName: Map<string, TargetMemoryInternal>,
    diagnostics: AssetMemoryDiagnostic[]
): boolean => {
    if (!memoryName) return true;

    const missingTargetNames: string[] = targetNames.filter(
        (targetName: string) => !targetInternalsByName.get(targetName)?.memoriesByName.has(memoryName)
    );

    if (missingTargetNames.length === 0) return true;

    diagnostics.push({
        severity: 'error',
        code: 'unknown-asset-memory',
        blockName,
        memoryName,
        message: `<${blockName}> references memory "${memoryName}", but it does not exist in target(s): ${missingTargetNames.join(
            ', '
        )}.`
    });
    return false;
};

/**
 * Converts an asset XML element into the normalized reference used by size resolvers and reports.
 * All original attributes are retained so estimators can inspect format-specific settings.
 */
export const createAssetReference = (
    blockName: AssetMemoryBlockName,
    assetElement: Element,
    memoryName: string | null,
    targetNames: string[]
): AssetMemoryAssetReference => {
    const attributes: Record<string, string> = Array.from(assetElement.attributes).reduce(
        (acc: Record<string, string>, attribute: Attr) => {
            acc[attribute.name] = attribute.value;
            return acc;
        },
        {}
    );

    return {
        assetType: blockName === 'images' ? 'image' : 'font',
        blockName,
        tagName: assetElement.tagName.toLowerCase() as AssetMemoryAssetReference['tagName'],
        name: assetElement.getAttribute('name')?.trim() || '',
        sourcePath: assetElement.getAttribute('src_path')?.trim() || '',
        attributes,
        memoryName,
        targetNames
    };
};
